import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Link } from 'wouter';
import type { MerchandiseProduct, InsertMerchandiseProduct } from '@shared/schema';
import AdminNavigation from '@/components/AdminNavigation';
import AdminFooter from '@/components/AdminFooter';

export default function AdminMerchandise() {
  const { toast } = useToast();
  const [editingProduct, setEditingProduct] = useState<MerchandiseProduct | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<InsertMerchandiseProduct>({
    name: '',
    slug: '',
    productType: '',
    category: '',
    description: '',
    price: '0',
    images: [],
    features: [],
    sizes: [],
    colors: [],
    inStock: true,
    featured: false,
    sortOrder: 0,
  });

  // Fetch all products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['/api/admin/merchandise'],
  });

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: async (data: InsertMerchandiseProduct) => {
      const response = await fetch('/api/admin/merchandise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create product');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Product created successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/merchandise'] });
      setIsCreating(false);
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Failed to create product', description: error.message, variant: 'destructive' });
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<MerchandiseProduct> }) => {
      const response = await fetch(`/api/admin/merchandise/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update product');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Product updated successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/merchandise'] });
      setEditingProduct(null);
    },
    onError: (error) => {
      toast({ title: 'Failed to update product', description: error.message, variant: 'destructive' });
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/merchandise/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete product');
      return response.json();
    },
    onSuccess: () => {
      toast({ title: 'Product deleted successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/merchandise'] });
    },
    onError: (error) => {
      toast({ title: 'Failed to delete product', description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      productType: '',
      category: '',
      description: '',
      price: '0',
      images: [],
      features: [],
      sizes: [],
      colors: [],
      inStock: true,
      featured: false,
      sortOrder: 0,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (product: MerchandiseProduct) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      productType: product.productType,
      category: product.category,
      description: product.description,
      price: product.price,
      images: product.images || [],
      features: product.features || [],
      sizes: product.sizes || [],
      colors: product.colors || [],
      inStock: product.inStock,
      featured: product.featured,
      sortOrder: product.sortOrder,
    });
  };

  // Available swag images
  const swagImages = [
    '/swag/SnuggleCrafter-baby-tee-1.png',
    '/swag/SnuggleCrafter-family-hoodie-1.png',
    '/swag/SnuggleCrafter-family-hoodie-2.png',
    '/swag/SnuggleCrafter-family-hoodie-3.png',
    '/swag/SnuggleCrafter-family-tee-1.png',
    '/swag/SnuggleCrafter-family-tee-2.png',
    '/swag/SnuggleCrafter-lil-tee-1.png',
    '/swag/SnuggleCrafter-lil-tee-2.png',
    '/swag/SnuggleCrafter-lil-tee-3.png',
    '/swag/SnuggleCrafter-lil-tee-4.png',
    '/swag/SnuggleCrafter-mug-17.50-1.png',
    '/swag/SnuggleCrafter-mug-17.50-23.png',
    '/swag/SnuggleCrafter-mug-17.50-2.png',
    '/swag/SnuggleCrafter-tee-1.png',
    '/swag/SnuggleCrafter-tee-2.png',
    '/swag/SnuggleCrafter-tee-3.png',
    '/swag/SnuggleCrafter-tee-4.png',
    '/swag/SnuggleCrafter-tee-5.png',
    '/swag/SnuggleCrafter-tote-25.75-1.png',
    '/swag/SnuggleCrafter-tote-25.75-2.png',
    '/swag/SnuggleCrafter-tote-25.75-3.png',
    '/swag/SnuggleCrafter-waterbottle-23.00-1.png',
    '/swag/SnuggleCrafter-waterbottle-23.00-2.png',
    '/swag/SnuggleCrafter-waterbottle-23.00-3.png',
    '/swag/SnuggleCrafter-woman-tee-1.png',
    '/swag/SnuggleCrafter-woman-tee-2.png',
    '/swag/SnuggleCrafter-woman-tee-3.png',
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-merchandise">
      <AdminNavigation currentPage="Merchandise" showBackButton={true} />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm md:text-base text-gray-600">Manage your merchandise products and inventory</p>
          </div>
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        </div>

      {isLoading ? (
        <div className="text-center py-8">Loading products...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(products as MerchandiseProduct[]).map((product: MerchandiseProduct) => (
            <Card key={product.id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-start">
                  <span>{product.name}</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(product)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteMutation.mutate(product.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {product.images && product.images[0] && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded mb-4"
                  />
                )}
                <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                <div className="flex justify-between items-center">
                  <span className="font-bold">${product.price}</span>
                  <div className="flex gap-2">
                    {product.featured && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Featured</span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${
                      product.inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {product.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreating || !!editingProduct} onOpenChange={(open) => {
        if (!open) {
          setIsCreating(false);
          setEditingProduct(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Edit Product' : 'Create New Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Product Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug (URL-friendly name)</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-generated-from-name"
                  onBlur={() => {
                    if (!formData.slug && formData.name) {
                      setFormData({
                        ...formData,
                        slug: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                      });
                    }
                  }}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="productType">Product Type</Label>
                <Select
                  value={formData.productType}
                  onValueChange={(value) => setFormData({ ...formData, productType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="t-shirt">T-Shirt</SelectItem>
                    <SelectItem value="hoodie">Hoodie</SelectItem>
                    <SelectItem value="mug">Mug</SelectItem>
                    <SelectItem value="tote">Tote Bag</SelectItem>
                    <SelectItem value="water-bottle">Water Bottle</SelectItem>
                    <SelectItem value="baby-tee">Baby Tee</SelectItem>
                    <SelectItem value="woman-tee">Women's Tee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apparel">Apparel</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="drinkware">Drinkware</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label>Product Images</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {swagImages.map((image) => (
                  <div
                    key={image}
                    className={`relative cursor-pointer border-2 rounded overflow-hidden ${
                      formData.images.includes(image) ? 'border-purple-500' : 'border-gray-200'
                    }`}
                    onClick={() => {
                      if (formData.images.includes(image)) {
                        setFormData({
                          ...formData,
                          images: formData.images.filter(img => img !== image)
                        });
                      } else {
                        setFormData({
                          ...formData,
                          images: [...formData.images, image]
                        });
                      }
                    }}
                  >
                    <img src={image} alt="" className="w-full h-24 object-cover" />
                    {formData.images.includes(image) && (
                      <div className="absolute inset-0 bg-purple-500 bg-opacity-20 flex items-center justify-center">
                        <div className="bg-purple-500 text-white rounded-full p-1">✓</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="features">Features (one per line)</Label>
              <Textarea
                id="features"
                value={formData.features?.join('\n') || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  features: e.target.value.split('\n').filter(f => f.trim()) 
                })}
                rows={3}
                placeholder="Premium cotton blend&#10;Machine washable&#10;Eco-friendly printing"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sizes">Sizes (comma-separated)</Label>
                <Input
                  id="sizes"
                  value={formData.sizes?.join(', ') || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    sizes: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                  })}
                  placeholder="S, M, L, XL, XXL"
                />
              </div>
              <div>
                <Label htmlFor="colors">Colors (comma-separated)</Label>
                <Input
                  id="colors"
                  value={formData.colors?.join(', ') || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    colors: e.target.value.split(',').map(c => c.trim()).filter(c => c) 
                  })}
                  placeholder="Black, White, Navy, Purple"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.inStock}
                  onCheckedChange={(checked) => setFormData({ ...formData, inStock: checked })}
                />
                <Label>In Stock</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                />
                <Label>Featured</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setEditingProduct(null);
                  resetForm();
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button type="submit">
                <Save className="mr-2 h-4 w-4" />
                {editingProduct ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
      <AdminFooter />
    </div>
  );
}